import { createSelector } from 'reselect'
import { unique } from 'lib/array/arrayUtils'
import { getViewedOrder } from 'selectors/orderSelectors'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import { isEsimExcludedCountry } from 'loyalty/lib/esims/esimGeoExclusion'

interface OrderEsimUpsellRowEligibility {
  isEligible: boolean
  countryCode: string | null
}

const ineligibleUpsellRow: OrderEsimUpsellRowEligibility = {
  isEligible: false,
  countryCode: null,
}

/**
 * Eligibility for the viewed order's eSIM upsell row, derived from `esimBenefit.countryCodes`
 * with upsell exclusion filters applied. Eligible only when exactly one international destination remains.
 */
const getCanShowOrderEsimUpsellRow = createSelector(
  (state: App.State) => getViewedOrder(state),
  (state: App.State) => getLoyaltyAccountRegion(state),
  (state: App.State) => state.loyalty.config?.esimExcludedCountryCodes ?? [],
  (order, accountRegion, esimExcludedCountryCodes): OrderEsimUpsellRowEligibility => {
    if (!order || order.status === 'cancelled' || !accountRegion) return ineligibleUpsellRow

    const countryCodes = order.loyaltyDetails?.esimBenefit?.countryCodes ?? []
    if (!countryCodes.length) return ineligibleUpsellRow

    const regionLower = accountRegion.toLowerCase()
    const eligible = unique(
      countryCodes
        .filter(cc => cc.toLowerCase() !== regionLower && !isEsimExcludedCountry(cc, esimExcludedCountryCodes))
        .map(cc => cc.toUpperCase()),
    )

    if (eligible.length !== 1) return ineligibleUpsellRow

    return {
      isEligible: true,
      countryCode: eligible[0],
    }
  },
)

export default getCanShowOrderEsimUpsellRow
